import { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Check, Copy, ExternalLink, Building2, User, Star, ChevronRight, ChevronLeft, Video, FileText } from 'lucide-react';
import { Button } from '@/components/ui/button';
import AgentPhoto from '@/components/AgentPhoto';
import { Textarea } from '@/components/ui/textarea';
import VideoRecorder from '@/components/VideoRecorder';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

const API = process.env.REACT_APP_BACKEND_URL 
  ? `${process.env.REACT_APP_BACKEND_URL}/api`
  : '/api';

const ALL_STEPS = [
  { id: 'dados', title: 'Dados', icon: User },
  { id: 'consultor', title: 'Consultor', icon: User },
  { id: 'avaliacao', title: 'Avaliação', icon: Star },
  { id: 'agencia', title: 'Agência', icon: Building2 },
  { id: 'concluido', title: 'Concluído', icon: Check },
];

const ProgressBar = ({ currentStep, steps }) => {
  return (
    <div className="w-full mb-6 md:mb-10 px-2" data-testid="progress-bar">
      <div className="flex items-center justify-center max-w-3xl mx-auto">
        {steps.map((step, index) => (
          <div key={step.id} className="flex items-center">
            <div className="flex flex-col items-center">
              <div
                className={`w-7 h-7 sm:w-10 sm:h-10 lg:w-12 lg:h-12 flex items-center justify-center border-2 transition-colors ${
                  currentStep >= index
                    ? 'bg-[#B5985A] border-[#B5985A] text-white'
                    : 'border-gray-300 text-gray-400'
                }`}
              >
                {currentStep > index ? (
                  <Check className="w-3.5 h-3.5 sm:w-5 sm:h-5 lg:w-6 lg:h-6" />
                ) : (
                  <step.icon className="w-3.5 h-3.5 sm:w-5 sm:h-5 lg:w-6 lg:h-6" />
                )}
              </div>
              <span
                className={`text-[9px] sm:text-xs lg:text-sm mt-1 sm:mt-2 font-medium uppercase tracking-tight sm:tracking-wide ${
                  currentStep >= index ? 'text-[#B5985A]' : 'text-gray-400'
                }`}
                style={{ fontFamily: 'Montserrat' }}
              >
                {step.title}
              </span>
            </div>
            {index < steps.length - 1 && (
              <div className="flex items-center mx-0.5 sm:mx-3 lg:mx-4">
                <ChevronRight
                  className={`w-3 h-3 sm:w-5 sm:h-5 lg:w-6 lg:h-6 ${
                    currentStep > index ? 'text-[#B5985A]' : 'text-gray-300'
                  }`}
                />
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

const NPSSelector = ({ value, onChange, testIdPrefix = "nps" }) => {
  const firstRow = [0, 1, 2, 3, 4, 5];
  const secondRow = [6, 7, 8, 9, 10];

  const renderButton = (num) => (
    <button
      key={num}
      type="button"
      onClick={() => onChange(num)}
      className={`nps-button ${value === num ? 'selected' : ''}`}
      data-testid={`${testIdPrefix}-button-${num}`}
    >
      {num}
    </button>
  );

  return (
    <div className="flex flex-col gap-2 sm:gap-3">
      {/* Primeira linha: 0-5 */}
      <div className="flex justify-center gap-2 sm:gap-3">
        {firstRow.map(renderButton)}
      </div>
      {/* Segunda linha: 6-10 */}
      <div className="flex justify-center gap-2 sm:gap-3">
        {secondRow.map(renderButton)}
      </div>
    </div>
  );
};

const AgentCard = ({ agent, selected, onClick }) => {
  return (
    <div
      onClick={onClick}
      className={`agent-card relative ${selected ? 'selected' : ''}`}
      data-testid={`agent-card-${agent.id}`}
    >
      {selected && (
        <div className="absolute top-2 right-2 w-6 h-6 bg-[#B5985A] flex items-center justify-center z-10">
          <Check className="w-4 h-4 text-white" />
        </div>
      )}
      <AgentPhoto src={agent.photo_url} alt={agent.name} size="lg" className="mb-3" />
      <h3 className="font-semibold text-[#212121] text-center text-sm md:text-base">
        {agent.name}
      </h3>
    </div>
  );
};

const RecommendationModal = ({ isOpen, onClose, text, targetUrl, type, agentGender, agentName, onComplete }) => {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setCopied(false);
    }
  }, [isOpen]);

  const handleCopyText = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success('Texto copiado!');
    } catch (err) {
      toast.error('Não foi possível copiar. Por favor seleccione o texto manualmente.');
    }
  };

  const handlePublish = () => {
    window.open(targetUrl, '_blank');
    onClose();
    if (onComplete) onComplete();
  };

  const handleSkip = () => {
    onClose();
    if (onComplete) onComplete();
  };

  const getConsultorText = () => {
    if (type === 'agency') return 'da agência';
    return agentGender === 'F' ? 'da consultora' : 'do consultor';
  };

  const getTitle = () => {
    if (type === 'agency') {
      return `Poderia contribuir com o seu comentário publicamente para a agência?`;
    }
    return `Poderia contribuir com o seu comentário publicamente para ${agentGender === 'F' ? 'a consultora' : 'o consultor'} ${agentName}?`;
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-[calc(100vw-2rem)] sm:max-w-md p-0 overflow-hidden" data-testid="recommendation-modal">
        <div className="p-4 sm:p-6 w-full" style={{ maxWidth: '100%', overflow: 'hidden' }}>
          <DialogHeader className="mb-4">
            <DialogTitle className="text-center font-bold text-base sm:text-lg leading-tight pr-4" style={{ wordBreak: 'break-word' }}>
              {getTitle()}
            </DialogTitle>
          </DialogHeader>
          <div className="flex flex-col items-center gap-3 sm:gap-4" style={{ maxWidth: '100%', overflow: 'hidden' }}>
            <p className="text-center text-gray-600 text-sm sm:text-base w-full" style={{ wordBreak: 'break-word' }}>
              Ao clicar no botão "Sim" será encaminhado para a página de recomendações pública {getConsultorText()} onde poderá deixar a mesma recomendação.
            </p>

            {/* Text box with copy button */}
            <div className="w-full" style={{ maxWidth: '100%' }}>
              <div className="relative">
                <div className="w-full bg-gray-50 p-2 sm:p-3 pr-10 sm:pr-12 rounded border text-xs sm:text-sm text-gray-700 min-h-[80px] max-h-32 overflow-y-auto overflow-x-hidden box-border" style={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}>
                  {text}
                </div>
                <button
                  onClick={handleCopyText}
                  className={`absolute top-1.5 sm:top-2 right-1.5 sm:right-2 p-1.5 sm:p-2 rounded transition-colors ${
                    copied
                      ? 'bg-green-100 text-green-600'
                      : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                  }`}
                  title={copied ? 'Copiado!' : 'Copiar texto'}
                >
                  {copied ? <Check className="w-3.5 h-3.5 sm:w-4 sm:h-4" /> : <Copy className="w-3.5 h-3.5 sm:w-4 sm:h-4" />}
                </button>
              </div>
              <p className="text-[10px] sm:text-xs text-gray-500 mt-1 text-center">
                {copied ? '✓ Texto copiado para a área de transferência' : 'Clique no ícone para copiar o texto'}
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 w-full" style={{ maxWidth: '100%' }}>
              <Button
                onClick={handleSkip}
                variant="outline"
                className="flex-1 text-xs sm:text-sm min-w-0 px-3 py-2 sm:px-4 sm:py-3 border-2 border-[#212121] text-[#212121] font-bold uppercase hover:bg-[#212121] hover:text-white box-border"
                data-testid="skip-recommendation-btn"
              >
                Não, obrigado
              </Button>
              <Button
                onClick={handlePublish}
                className="flex-1 flex items-center justify-center gap-2 text-xs sm:text-sm min-w-0 px-3 py-2 sm:px-4 sm:py-3 bg-[#B5985A] text-black font-bold uppercase hover:bg-[#A3884A] box-border"
                data-testid="publish-btn"
              >
                <ExternalLink className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                Sim, publicar
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default function ReviewPage() {
  const [searchParams] = useSearchParams();
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentStep, setCurrentStep] = useState(0);
  const [reviewerName, setReviewerName] = useState('');
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [agentPreselected, setAgentPreselected] = useState(false);
  const [agencyNps, setAgencyNps] = useState(null);
  const [agentNps, setAgentNps] = useState(null);
  const [agencyReview, setAgencyReview] = useState('');
  const [agentReview, setAgentReview] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [modalConfig, setModalConfig] = useState({ text: '', url: '', type: '' });
  const [evaluationId, setEvaluationId] = useState(null);
  const [agentSubmitted, setAgentSubmitted] = useState(false);
  const [agencySubmitted, setAgencySubmitted] = useState(false);
  const [lastSavedAgent, setLastSavedAgent] = useState({ nps: null, review: '' });
  const [lastSavedAgency, setLastSavedAgency] = useState({ nps: null, review: '' });
  const [agentRecommendationShown, setAgentRecommendationShown] = useState(false);
  const [agencyRecommendationShown, setAgencyRecommendationShown] = useState(false);
  const [showConfirmReset, setShowConfirmReset] = useState(false);
  const [reviewMode, setReviewMode] = useState('text'); // 'text' or 'video'
  const [videoUrl, setVideoUrl] = useState(null);

  // Dynamic steps: skip 'consultor' when agent is preselected via URL
  const steps = useMemo(() => {
    if (agentPreselected) {
      return ALL_STEPS.filter(s => s.id !== 'consultor');
    }
    return ALL_STEPS;
  }, [agentPreselected]);

  // Get the step type (id) for the current index
  const currentStepId = steps[currentStep]?.id;

  // Load agents on mount, check for preselected agent via URL param
  useEffect(() => {
    fetchAgents();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchAgents = async () => {
    try {
      const response = await axios.get(`${API}/agents/public`);
      const agentsList = response.data;
      setAgents(agentsList);

      // Check for ?id=<slug> URL parameter
      const slugParam = searchParams.get('id');
      if (slugParam) {
        const preselected = agentsList.find(a => a.slug === slugParam);
        if (preselected) {
          setSelectedAgent(preselected);
          setAgentPreselected(true);
        }
      }
    } catch (error) {
      console.error('Error fetching agents:', error);
      toast.error('Erro ao carregar consultores');
    } finally {
      setLoading(false);
    }
  };

  const canProceed = () => {
    switch (currentStepId) {
      case 'dados':
        return reviewerName.trim().length >= 2;
      case 'consultor':
        return selectedAgent !== null;
      case 'avaliacao':
        if (reviewMode === 'video') return agentNps !== null && videoUrl !== null;
        return agentNps !== null && agentReview.length >= 20;
      case 'agencia':
        return agencyNps !== null && agencyReview.length >= 20;
      default:
        return false;
    }
  };

  const handleNext = async () => {
    if (!canProceed() || currentStepId === 'agencia' || currentStepId === 'concluido') return;

    // Avaliação step: save and optionally show modal
    const agentReviewValid = reviewMode === 'video' ? videoUrl !== null : agentReview.length >= 20;
    if (currentStepId === 'avaliacao' && agentNps !== null && agentReviewValid) {
      // Show modal only if NPS >= 8 and user hasn't already used "Avaliar Consultor"
      if (agentNps >= 8 && !agentRecommendationShown) {
        const evalId = await submitAgentEvaluation();

        let finalText;
        if (reviewMode === 'video') {
          // Wait for transcription to complete
          try {
            const transcription = await waitForTranscription(evalId);
            finalText = `"${transcription}"\n\nVeja a minha avaliação em vídeo: ${videoUrl}`;
          } catch (error) {
            console.error('Transcription timeout or error:', error);
            // Fallback if transcription fails or times out
            finalText = `Veja a minha avaliação em vídeo: ${videoUrl}`;
            toast.warning('A transcrição está a demorar. O vídeo está disponível para partilha.');
          }
        } else {
          finalText = agentReview;
        }

        setModalConfig({
          text: finalText,
          url: selectedAgent.review_url,
          type: 'agent',
        });
        setShowModal(true);
        return; // Don't advance yet - modal will advance when closed
      } else {
        submitAgentEvaluation(); // fire and forget
      }
    }

    setCurrentStep(currentStep + 1);
  };

  const handlePrev = () => {
    if (currentStep <= 0) return;

    // Save current step data async (fire and forget) before going back
    const agentValid = reviewMode === 'video' ? videoUrl !== null : agentReview.length >= 20;
    if (currentStepId === 'avaliacao' && selectedAgent && agentNps !== null && agentValid) {
      submitAgentEvaluation(); // async, don't await
    }
    if (currentStepId === 'agencia' && evaluationId && agencyNps !== null && agencyReview.length >= 20) {
      submitAgencyEvaluation(); // async, don't await
    }

    // Reset recommendation flags — values may change before next forward navigation
    const prevStepId = steps[currentStep - 1]?.id;
    if (prevStepId === 'avaliacao') setAgentRecommendationShown(false);
    if (prevStepId === 'agencia') setAgencyRecommendationShown(false);

    setCurrentStep(currentStep - 1);
  };

  const getConsultorLabel = () => {
    if (!selectedAgent) return 'Consultor(a)';
    return selectedAgent.gender === 'F' ? 'Consultora' : 'Consultor';
  };

  const getConsultorPrefixLabel = () => {
    if (!selectedAgent) return 'O seu consultor:';
    return selectedAgent.gender === 'F' ? 'A sua consultora:' : 'O seu consultor:';
  };

  // Poll for transcription completion
  const waitForTranscription = async (evalId, maxAttempts = 60) => {
    console.log(`[waitForTranscription] Starting polling for evaluation ${evalId}`);

    for (let i = 0; i < maxAttempts; i++) {
      try {
        console.log(`[waitForTranscription] Attempt ${i + 1}/${maxAttempts}`);
        const response = await axios.get(`${API}/evaluations/${evalId}`);
        const review = response.data.agent_review;
        console.log(`[waitForTranscription] Current review:`, review?.substring(0, 100));

        // Check if transcription is complete (not the placeholder message)
        if (review && !review.includes('transcrição em processamento') && !review.includes('Erro na transcrição')) {
          console.log(`[waitForTranscription] Transcription complete!`);
          return review;
        }

        // Check for error
        if (review && review.includes('Erro na transcrição')) {
          console.error('[waitForTranscription] Transcription failed');
          throw new Error('Transcription failed');
        }

        // Wait 2 seconds before next attempt
        await new Promise(resolve => setTimeout(resolve, 2000));
      } catch (error) {
        console.error('[waitForTranscription] Error polling:', error);
        // Continue polling even on error unless it's a 404
        if (error.response?.status === 404) {
          throw new Error('Evaluation not found');
        }
      }
    }
    console.error('[waitForTranscription] Timeout after', maxAttempts, 'attempts');
    throw new Error('Transcription timeout');
  };

  const submitAgentEvaluation = async () => {
    const reviewText = reviewMode === 'video'
      ? '(Vídeo - transcrição em processamento...)'
      : (agentReview || '');

    try {
      // If already submitted, update existing record only if data changed
      if (agentSubmitted && evaluationId) {
        if (lastSavedAgent.nps === agentNps && lastSavedAgent.review === reviewText) {
          return evaluationId; // No changes, skip API call
        }
        await axios.put(`${API}/evaluations/${evaluationId}`, {
          agent_nps: agentNps ?? 0,
          agent_review: reviewText,
          agent_review_video: videoUrl || null,
        });
        setLastSavedAgent({ nps: agentNps, review: reviewText });
        return evaluationId;
      }

      // Create new evaluation
      const response = await axios.post(`${API}/evaluations`, {
        agent_id: selectedAgent.id,
        reviewer_name: reviewerName,
        agent_nps: agentNps ?? 0,
        agent_review: reviewText,
        agent_review_video: videoUrl || null,
      });
      const evalId = response.data.id;
      setEvaluationId(evalId);
      setAgentSubmitted(true);
      setLastSavedAgent({ nps: agentNps, review: reviewText });

      // If video mode, trigger async transcription (fire and forget)
      if (reviewMode === 'video' && videoUrl) {
        fetch(`${API}/transcribe-video`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ evaluation_id: evalId, video_url: videoUrl }),
        }).catch(err => console.error('Failed to trigger transcription:', err));
      }

      return evalId;
    } catch (error) {
      console.error('Error submitting agent evaluation:', error);
      toast.error('Erro ao submeter avaliação do consultor. Por favor tente novamente.');
      return null;
    }
  };

  const submitAgencyEvaluation = async () => {
    if (!evaluationId) {
      toast.error('Erro: avaliação do consultor não foi encontrada.');
      return false;
    }

    try {
      // Skip if data hasn't changed since last save
      if (agencySubmitted && lastSavedAgency.nps === agencyNps && lastSavedAgency.review === agencyReview) {
        return true; // No changes, skip API call
      }

      await axios.put(`${API}/evaluations/${evaluationId}`, {
        agency_nps: agencyNps ?? 0,
        agency_review: agencyReview || '',
      });
      setAgencySubmitted(true);
      setLastSavedAgency({ nps: agencyNps, review: agencyReview });
      return true;
    } catch (error) {
      console.error('Error submitting agency evaluation:', error);
      toast.error('Erro ao submeter avaliação da agência. Por favor tente novamente.');
      return false;
    }
  };

  const stepIndex = (id) => steps.findIndex(s => s.id === id);

  const handleEvaluateAgent = async () => {
    if (!selectedAgent || agentNps === null) return;
    if (reviewMode === 'text' && agentReview.length < 20) return;
    if (reviewMode === 'video' && !videoUrl) return;

    setSubmitting(true);
    try {
      const evalId = await submitAgentEvaluation();
      setAgentRecommendationShown(true);

      let finalText;
      if (reviewMode === 'video') {
        // Wait for transcription to complete
        toast.info('A aguardar transcrição do vídeo...', { duration: Infinity, id: 'transcription-wait' });
        try {
          const transcription = await waitForTranscription(evalId);
          toast.dismiss('transcription-wait');
          toast.success('Transcrição completa!');
          finalText = `"${transcription}"\n\nVeja a minha avaliação em vídeo: ${videoUrl}`;
        } catch (error) {
          console.error('Transcription timeout or error:', error);
          toast.dismiss('transcription-wait');
          // Fallback if transcription fails or times out
          finalText = `Veja a minha avaliação em vídeo: ${videoUrl}`;
          toast.warning('A transcrição está a demorar. O vídeo está disponível para partilha.');
        }
      } else {
        finalText = agentReview;
      }

      setModalConfig({
        text: finalText,
        url: selectedAgent.review_url,
        type: 'agent',
      });
      setShowModal(true);
    } catch (error) {
      console.error('Error:', error);
      toast.error('Ocorreu um erro. Por favor tente novamente.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEvaluateAgency = async () => {
    if (agencyNps === null || agencyReview.length < 20) return;

    setSubmitting(true);
    try {
      await submitAgencyEvaluation();
      setAgencyRecommendationShown(true);
      const agencyResponse = await axios.get(`${API}/agencies/public/${selectedAgent.agency_id}`);
      setModalConfig({
        text: agencyReview,
        url: agencyResponse.data.review_url,
        type: 'agency',
      });
      setShowModal(true);
    } catch (error) {
      console.error('Error:', error);
      toast.error('Ocorreu um erro. Por favor tente novamente.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleFinish = async () => {
    if (agencyNps === null || agencyReview.length < 20) return;

    // Show modal only if NPS >= 8 and user hasn't already used "Avaliar Agência"
    if (agencyNps >= 8 && !agencyRecommendationShown) {
      setSubmitting(true);
      try {
        await submitAgencyEvaluation();
        const agencyResponse = await axios.get(`${API}/agencies/public/${selectedAgent.agency_id}`);
        setModalConfig({
          text: agencyReview,
          url: agencyResponse.data.review_url,
          type: 'agency',
        });
        setShowModal(true);
      } catch (error) {
        console.error('Error:', error);
        toast.error('Ocorreu um erro. Por favor tente novamente.');
      } finally {
        setSubmitting(false);
      }
    } else {
      submitAgencyEvaluation(); // fire and forget
      setCurrentStep(stepIndex('concluido'));
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8F8F8]">
        <div className="w-8 h-8 border-2 border-[#B5985A] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white overflow-x-hidden flex flex-col" data-testid="review-page">
      {/* Header */}
      <header className="bg-[#212121] py-4 px-4 sm:px-6">
        <div className="max-w-4xl mx-auto flex items-center gap-3">
          <img
            src="https://customer-assets.emergentagent.com/job_reviewfunnel/artifacts/wxdmy5mp_Opini%C3%A3o%20Contacto%20Directo%20%281%29.svg"
            alt="Opinião Contacto Directo"
            className="h-8 sm:h-10 max-w-full object-contain"
          />
        </div>
      </header>

      <main className="flex-grow max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6 md:py-12 w-full">
        {/* Title */}
        <div className="text-center mb-6 md:mb-10 animate-slide-up">
          <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-[#212121] mb-2 md:mb-4" style={{ fontFamily: 'Montserrat' }}>
            Partilhe a sua experiência
          </h1>
          <p className="text-sm sm:text-base lg:text-lg text-gray-600">
            A sua opinião ajuda-nos a melhorar continuamente os nossos serviços
          </p>
        </div>

        {/* Progress Bar */}
        <ProgressBar currentStep={currentStep} steps={steps} />

        {/* Step: Reviewer Name */}
        {currentStepId === 'dados' && (
          <section className="animate-slide-up" data-testid="step-0">
            <div className="flex items-center justify-center sm:justify-start gap-2 mb-4">
              <User className="w-4 h-4 text-[#B5985A]" />
              <span className="label-uppercase">Os seus dados</span>
            </div>
            <div className="mb-8">
              <label htmlFor="reviewer-name" className="block text-sm font-medium text-gray-700 mb-2">
                O seu nome <span className="text-red-500">*</span>
              </label>
              <input
                id="reviewer-name"
                type="text"
                value={reviewerName}
                onChange={(e) => setReviewerName(e.target.value)}
                placeholder="Insira o seu nome, por favor"
                className="w-full px-4 py-3 border-2 border-gray-200 focus:border-[#B5985A] focus:outline-none rounded-none"
                data-testid="reviewer-name-input"
              />
              <p className={`text-xs mt-1 ${reviewerName.trim().length < 2 ? 'text-gray-400' : 'text-green-600'}`}>
                {reviewerName.trim().length < 2 ? 'Mínimo de 2 caracteres' : '✓ Nome válido'}
              </p>
            </div>
          </section>
        )}

        {/* Step: Agent Selection */}
        {currentStepId === 'consultor' && (
          <section className="animate-slide-up" data-testid="step-1">
            <div className="flex items-center justify-center sm:justify-start gap-2 mb-4">
              <User className="w-4 h-4 text-[#B5985A]" />
              <span className="label-uppercase">Seleccione o seu consultor</span>
            </div>
            {agents.length > 0 ? (
              <>
                {!selectedAgent ? (
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4 stagger-children">
                    {agents.map((agent) => (
                      <AgentCard
                        key={agent.id}
                        agent={agent}
                        selected={false}
                        onClick={() => setSelectedAgent(agent)}
                      />
                    ))}
                  </div>
                ) : (
                  <div className="space-y-4">
                    {/* Selected agent display */}
                    <div className="agent-card selected">
                      <div className="absolute top-2 right-2 w-6 h-6 bg-[#B5985A] flex items-center justify-center z-10">
                        <Check className="w-4 h-4 text-white" />
                      </div>
                      <AgentPhoto src={selectedAgent.photo_url} alt={selectedAgent.name} size="lg" className="mb-3" />
                      <h3 className="font-semibold text-[#212121] text-center text-sm md:text-base">
                        {selectedAgent.name}
                      </h3>
                    </div>
                    {/* Change selection button */}
                    <button
                      onClick={() => setSelectedAgent(null)}
                      className="w-full border-2 border-gray-300 hover:border-[#B5985A] p-4 text-gray-600 hover:text-[#B5985A] transition-colors flex items-center justify-center gap-2"
                    >
                      <User className="w-4 h-4" />
                      <span className="text-sm font-medium">Seleccionar outro consultor</span>
                    </button>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-8 bg-gray-50 border border-gray-200">
                <p className="text-gray-500">Nenhum consultor disponível de momento</p>
              </div>
            )}
          </section>
        )}

        {/* Step: Agent NPS & Review */}
        {currentStepId === 'avaliacao' && (
          <section className="animate-slide-up space-y-6" data-testid="step-2">
            {/* Selected Agent Summary */}
            {selectedAgent && (
              <div className="p-4 bg-gray-50 border border-gray-200 mb-8">
                <p className="text-sm text-gray-500 mb-2">{getConsultorPrefixLabel()}</p>
                <div className="flex items-center gap-3">
                  <AgentPhoto src={selectedAgent.photo_url} alt={selectedAgent.name} size="md" />
                  <p className="font-semibold text-[#212121] text-lg">{selectedAgent.name}</p>
                </div>
              </div>
            )}

            {/* Agent NPS */}
            <div className="mb-8 pt-4">
              <div className="flex items-center justify-center sm:justify-start gap-2 mb-3">
                <Star className="w-4 h-4 lg:w-5 lg:h-5 text-[#B5985A]" />
                <span className="label-uppercase">Pontuação {selectedAgent?.gender === 'F' ? 'da Consultora' : 'do Consultor'}</span>
              </div>
              <p className="text-gray-700 mb-3 text-center text-sm">
                Numa escala de 0 a 10, qual a probabilidade de recomendar {selectedAgent?.gender === 'F' ? 'a consultora' : 'o consultor'} {selectedAgent?.name}?
              </p>
              <p className="text-gray-600 mb-4 text-center text-xs max-w-full">
                Sendo 0 <span className="bg-[#B5985A] text-black px-1.5 sm:px-2 py-0.5 rounded font-semibold whitespace-nowrap">Nada Provável</span> e 10 <span className="bg-[#B5985A] text-black px-1.5 sm:px-2 py-0.5 rounded font-semibold whitespace-nowrap">Muito Provável</span>
              </p>
              <NPSSelector value={agentNps} onChange={setAgentNps} testIdPrefix="agent-nps" />
            </div>

            {/* Agent Review - Mode Toggle */}
            <div className="mb-8 pt-10">
              <div className="flex items-center justify-center sm:justify-start gap-2 mb-3">
                <User className="w-4 h-4 lg:w-5 lg:h-5 text-[#B5985A]" />
                <span className="label-uppercase">Comentário sobre {selectedAgent?.gender === 'F' ? 'a Consultora' : 'o Consultor'}</span>
              </div>

              {/* Mode toggle buttons - hidden once video is uploaded */}
              {!videoUrl && (
                <div className="flex gap-2 mb-4 justify-center sm:justify-start">
                  <button
                    type="button"
                    onClick={() => setReviewMode('text')}
                    className={`flex items-center gap-2 px-4 py-2 border-2 text-sm font-medium transition-colors ${
                      reviewMode === 'text'
                        ? 'border-[#B5985A] bg-[#B5985A]/10 text-[#B5985A]'
                        : 'border-gray-200 text-gray-500 hover:border-gray-300'
                    }`}
                  >
                    <FileText className="w-4 h-4" />
                    Escrever texto
                  </button>
                  <button
                    type="button"
                    onClick={() => setReviewMode('video')}
                    className={`flex items-center gap-2 px-4 py-2 border-2 text-sm font-medium transition-colors ${
                      reviewMode === 'video'
                        ? 'border-[#B5985A] bg-[#B5985A]/10 text-[#B5985A]'
                        : 'border-gray-200 text-gray-500 hover:border-gray-300'
                    }`}
                  >
                    <Video className="w-4 h-4" />
                    Gravar vídeo
                  </button>
                </div>
              )}

              {/* Text mode */}
              {reviewMode === 'text' && (
                <>
                  <Textarea
                    value={agentReview}
                    onChange={(e) => setAgentReview(e.target.value)}
                    placeholder={`Fale-nos da sua experiência com ${selectedAgent?.gender === 'F' ? 'a sua consultora' : 'o seu consultor'}...`}
                    className="min-h-[120px] border-2 border-gray-200 focus:border-[#B5985A] rounded-none resize-none"
                    data-testid="agent-review-textarea"
                  />
                  <p className={`text-xs mt-1 ${agentReview.length < 20 ? 'text-gray-400' : 'text-green-600'}`}>
                    {agentReview.length}/20 caracteres mínimos
                  </p>
                </>
              )}

              {/* Video mode */}
              {reviewMode === 'video' && (
                <VideoRecorder
                  onVideoReady={(url) => setVideoUrl(url)}
                  onCancel={() => setReviewMode('text')}
                  disabled={submitting}
                />
              )}
            </div>

            {/* Evaluate Agent Button */}
            <div className="pt-4">
              <Button
                onClick={handleEvaluateAgent}
                disabled={
                  submitting ||
                  agentNps === null ||
                  (reviewMode === 'text' && agentReview.length < 20) ||
                  (reviewMode === 'video' && !videoUrl)
                }
                className="btn-gold w-full flex items-center justify-center gap-2"
                data-testid="evaluate-agent-btn"
              >
                <User className="w-4 h-4" />
                Avaliar {getConsultorLabel()}
              </Button>
            </div>
          </section>
        )}

        {/* Step: Agency NPS & Review */}
        {currentStepId === 'agencia' && (
          <section className="animate-slide-up space-y-6" data-testid="step-3">
            {/* Agency NPS */}
            <div className="mb-8 pt-4">
              <div className="flex items-center justify-center sm:justify-start gap-2 mb-3">
                <Star className="w-4 h-4 lg:w-5 lg:h-5 text-[#B5985A]" />
                <span className="label-uppercase">Pontuação da Agência</span>
              </div>
              <p className="text-gray-700 mb-3 text-center text-sm">
                Numa escala de 0 a 10, qual a probabilidade de recomendar a nossa agência?
              </p>
              <p className="text-gray-600 mb-4 text-center text-xs max-w-full">
                Sendo 0 <span className="bg-[#B5985A] text-black px-1.5 sm:px-2 py-0.5 rounded font-semibold whitespace-nowrap">Nada Provável</span> e 10 <span className="bg-[#B5985A] text-black px-1.5 sm:px-2 py-0.5 rounded font-semibold whitespace-nowrap">Muito Provável</span>
              </p>
              <NPSSelector value={agencyNps} onChange={setAgencyNps} testIdPrefix="agency-nps" />
            </div>

            {/* Agency Review */}
            <div className="mb-8 pt-10">
              <div className="flex items-center justify-center sm:justify-start gap-2 mb-3">
                <Building2 className="w-4 h-4 lg:w-5 lg:h-5 text-[#B5985A]" />
                <span className="label-uppercase">Comentário sobre a Agência</span>
              </div>
              <Textarea
                value={agencyReview}
                onChange={(e) => setAgencyReview(e.target.value)}
                placeholder="Fale-nos da sua experiência com a agência e a nossa equipa..."
                className="min-h-[120px] border-2 border-gray-200 focus:border-[#B5985A] rounded-none resize-none"
                data-testid="agency-review-textarea"
              />
              <p className={`text-xs mt-1 ${agencyReview.length < 20 ? 'text-gray-400' : 'text-green-600'}`}>
                {agencyReview.length}/20 caracteres mínimos
              </p>
            </div>

            {/* Evaluate Agency Button */}
            <div className="pt-4">
              <Button
                onClick={handleEvaluateAgency}
                disabled={submitting || agencyNps === null || agencyReview.length < 20}
                className="btn-black w-full flex items-center justify-center gap-2"
                data-testid="evaluate-agency-btn"
              >
                <Building2 className="w-4 h-4" />
                Avaliar Agência
              </Button>
            </div>
          </section>
        )}

        {/* Step: Thank You */}
        {currentStepId === 'concluido' && (
          <section className="animate-slide-up" data-testid="step-4">
            <div className="text-center py-12">
              <div className="w-20 h-20 bg-[#B5985A] rounded-full flex items-center justify-center mx-auto mb-6">
                <Check className="w-10 h-10 text-white" />
              </div>
              <h2 className="text-2xl md:text-3xl font-bold text-[#212121] mb-4" style={{ fontFamily: 'Montserrat' }}>
                Muito Obrigado!
              </h2>
              <p className="text-gray-600 mb-6 max-w-md mx-auto">
                A sua opinião é muito importante para nós e ajuda-nos a melhorar continuamente os nossos serviços.
              </p>
              <p className="text-gray-500 text-sm mb-8">
                Agradecemos o tempo que dedicou a partilhar a sua experiência connosco.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center items-center">
                <Button
                  onClick={() => { setAgencyRecommendationShown(false); setCurrentStep(stepIndex('agencia')); }}
                  variant="outline"
                  className="btn-outline w-full sm:w-auto flex items-center justify-center gap-2"
                  data-testid="edit-review-btn"
                >
                  <ChevronLeft className="w-4 h-4" />
                  Voltar
                </Button>
                <Button
                  onClick={() => setShowConfirmReset(true)}
                  variant="outline"
                  className="btn-outline w-full sm:w-auto"
                  data-testid="restart-review-btn"
                >
                  Nova avaliação
                </Button>
              </div>
            </div>
          </section>
        )}

        {/* Navigation Buttons */}
        {currentStepId !== 'concluido' && (
          <div className="flex justify-between mt-8 pt-6 border-t border-gray-200">
            <Button
              onClick={handlePrev}
              disabled={currentStep === 0}
              variant="outline"
              className={`btn-outline flex items-center gap-2 ${currentStep === 0 ? 'invisible' : ''}`}
              data-testid="prev-step-btn"
            >
              <ChevronLeft className="w-4 h-4" />
              Anterior
            </Button>

            {currentStepId !== 'agencia' && (
              <Button
                onClick={handleNext}
                disabled={!canProceed()}
                className="btn-gold flex items-center gap-2"
                data-testid="next-step-btn"
              >
                Seguinte
                <ChevronRight className="w-4 h-4" />
              </Button>
            )}

            {currentStepId === 'agencia' && (
              <Button
                onClick={handleFinish}
                disabled={submitting || agencyNps === null || agencyReview.length < 20}
                className="btn-gold flex items-center gap-2"
                data-testid="finish-btn"
              >
                Finalizar
                <Check className="w-4 h-4" />
              </Button>
            )}

          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="bg-[#F8F8F8] py-6 border-t border-gray-200">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <p className="text-sm text-gray-500">
            © {new Date().getFullYear()} Opinião Contacto Directo. Todos os direitos reservados.
          </p>
        </div>
      </footer>

      {/* Recommendation Modal */}
      <RecommendationModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        text={modalConfig.text}
        targetUrl={modalConfig.url}
        type={modalConfig.type}
        agentGender={selectedAgent?.gender}
        agentName={selectedAgent?.name}
        onComplete={() => {
          // Advance to next step after redirect
          if (modalConfig.type === 'agent' && currentStepId === 'avaliacao') {
            setCurrentStep(stepIndex('agencia'));
          } else if (modalConfig.type === 'agency' && currentStepId === 'agencia') {
            setCurrentStep(stepIndex('concluido'));
          }
        }}
      />

      {/* Confirm Reset Modal */}
      <Dialog open={showConfirmReset} onOpenChange={setShowConfirmReset}>
        <DialogContent className="w-[calc(100vw-2rem)] max-w-md p-4 sm:p-6 box-border" data-testid="confirm-reset-modal">
          <DialogHeader className="pr-6">
            <DialogTitle className="text-center font-bold text-base sm:text-lg">
              Tem a certeza?
            </DialogTitle>
          </DialogHeader>
          <div className="flex flex-col items-center py-2 sm:py-4 gap-3 sm:gap-4">
            <p className="text-center text-gray-600 text-xs sm:text-sm">
              Tem a certeza que quer introduzir outra avaliação? Todos os dados da avaliação atual serão perdidos.
            </p>
            <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 w-full">
              <Button
                onClick={() => setShowConfirmReset(false)}
                variant="outline"
                className="flex-1 min-w-0 text-xs sm:text-sm py-2.5 px-4 border-2 border-[#212121] text-[#212121] font-bold uppercase hover:bg-[#212121] hover:text-white"
                data-testid="cancel-reset-btn"
              >
                Cancelar
              </Button>
              <Button
                onClick={() => {
                  window.location.reload();
                }}
                className="flex-1 min-w-0 text-xs sm:text-sm py-2.5 px-4 bg-[#B5985A] text-black font-bold uppercase hover:bg-[#A3884A]"
                data-testid="confirm-reset-btn"
              >
                Sim, nova avaliação
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
